import { useEffect, useState, type CSSProperties } from 'react';
import { remove } from 'aws-amplify/storage';
import { CompanyResource, RESOURCE_CATEGORIES, CompanyResourceCategory, CompanyResourceFileType } from '../../types/companyResourceTypes';
import type { CompanyResource as Gen2CompanyResource } from '../../types/schema';
import { isTrainingCategory, mapFileTypeToTopicStyle, inferFileType } from '../../utils/companyResourceUtils';
import {
    listCompanyResourcesByCompanyId,
    updateCompanyResource as updateCompanyResourceMut,
    listLegacyDocumentsByCompany,
    listLegacyVideosByCompany,
    createCompanyResource,
    deleteLegacyDocument,
    deleteLegacyVideo,
    updateLegacyDocumentFolder,
    updateLegacyVideoFolder,
    listFormTemplateGroupsByCompany,
    listFormTemplatesByGroup,
    removeCompanyFromTemplateGroup,
} from '../../graphql/companyResourceQueries';
import {
    createTrainingTopic,
    updateTrainingTopic,
} from '../../graphql/trainingQueries';
import { ResourceModal } from './ResourceModal';
import { DeleteResourceModal } from './DeleteResourceModal';
import { DeleteLegacyResourceModal } from './DeleteLegacyResourceModal';
import { AssignFormsModal } from './AssignFormsModal';
import TopicContentViewerModal from '../safety-training/TopicContentViewerModal';
import { AccordionCard, Btn, IconPlus, IconTrash } from '../ui';
import type { AccordionCardItem } from '../ui';
import { v4 as uuidv4 } from 'uuid';

interface CompanyResourcesTabProps {
    isAdmin: boolean;
    companyId: string;
}

/** Unified display item — either a new CompanyResource or a legacy Document/Video */
interface DisplayResource {
    id: string;
    title: string;
    fileType: string;
    category: string;
    folderName?: string | null;
    contentFilePath?: string | null;
    contentUrl?: string | null;
    trainingTopicId?: string | null;
    createdAt?: string;
    _source: 'new' | 'legacy-doc' | 'legacy-video';
    _legacyId?: string;
    _original?: CompanyResource;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _legacyRecord?: any;
}

const FILE_TYPE_ICONS: Record<string, string> = {
    PDF: '📄', Video: '🎬', Image: '🖼️', Excel: '📊', Document: '📝',
};
const FILE_TYPE_BADGE_CLASS: Record<string, string> = {
    PDF: 'cr-badge--pdf', Video: 'cr-badge--video', Image: 'cr-badge--image',
    Excel: 'cr-badge--excel', Document: 'cr-badge--document',
};

function legacyDocToDisplay(doc: any): DisplayResource {
    return {
        id: `legacy-doc-${doc.id}`,
        title: doc.title,
        fileType: inferFileType(doc.contentPath || doc.title || ''),
        category: 'Training Materials',
        folderName: doc.folderName || null,
        contentFilePath: doc.contentPath || null,
        trainingTopicId: null,
        createdAt: doc.createdAt,
        _source: 'legacy-doc',
        _legacyId: doc.id,
        _legacyRecord: doc,
    };
}

function legacyVideoToDisplay(vid: any): DisplayResource {
    return {
        id: `legacy-vid-${vid.id}`,
        title: vid.title,
        fileType: 'Video',
        category: 'Training Materials',
        folderName: vid.folderName || null,
        contentFilePath: vid.contentPath || null,
        trainingTopicId: null,
        createdAt: vid.createdAt,
        _source: 'legacy-video',
        _legacyId: vid.id,
        _legacyRecord: vid,
    };
}

function newResourceToDisplay(r: Gen2CompanyResource): DisplayResource {
    const original: CompanyResource = {
        id: r.id,
        companyId: r.companyId,
        title: r.title,
        description: r.description,
        category: r.category as CompanyResourceCategory,
        fileType: r.fileType as CompanyResourceFileType,
        contentFilePath: r.contentFilePath,
        contentUrl: r.contentUrl,
        folderName: r.folderName,
        trainingTopicId: r.trainingTopicId,
        uploadedByUserId: r.uploadedByUserId,
        uploadedByUserName: r.uploadedByUserName,
        createdAt: r.createdAt,
        updatedAt: r.updatedAt,
    };
    return {
        id: r.id, title: r.title, fileType: r.fileType, category: r.category,
        folderName: r.folderName || null, contentFilePath: r.contentFilePath,
        contentUrl: r.contentUrl || null,
        trainingTopicId: r.trainingTopicId, createdAt: r.createdAt,
        _source: 'new', _original: original,
    };
}

/** Within a category, group items by fileType then by folder */
interface FolderGroup { name: string; items: DisplayResource[]; }
interface TypeSubGroup { fileType: string; folders: FolderGroup[]; }

const naturalCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

function groupItemsByTypeAndFolder(items: DisplayResource[]): TypeSubGroup[] {
    const typeOrder = ['PDF', 'Video', 'Image', 'Excel', 'Document'];
    const byType: Record<string, DisplayResource[]> = {};
    for (const item of items) {
        const t = item.fileType || 'Document';
        if (!byType[t]) byType[t] = [];
        byType[t].push(item);
    }
    return typeOrder
        .filter(t => byType[t]?.length)
        .map(t => {
            const typeItems = byType[t];
            const folderMap: Record<string, DisplayResource[]> = {};
            for (const item of typeItems) {
                const f = item.folderName || 'Unfiled';
                if (!folderMap[f]) folderMap[f] = [];
                folderMap[f].push(item);
            }
            const folders = Object.entries(folderMap)
                .sort(([a], [b]) => naturalCollator.compare(a, b))
                .map(([name, items]) => ({
                    name,
                    items: items.sort((a, b) => naturalCollator.compare(a.title, b.title)),
                }));
            return { fileType: t, folders };
        });
}

export const CompanyResourcesTab = ({ isAdmin, companyId }: CompanyResourcesTabProps) => {
    const [displayItems, setDisplayItems] = useState<DisplayResource[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [editingResource, setEditingResource] = useState<CompanyResource | null>(null);
    const [deleteResource, setDeleteResource] = useState<CompanyResource | null>(null);
    const [deletingLegacy, setDeletingLegacy] = useState<DisplayResource | null>(null);
    const [isDeletingLegacy, setIsDeletingLegacy] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [expandedIds, setExpandedIds] = useState<string[]>(() =>
        RESOURCE_CATEGORIES.map((c) => c.name),
    );
    const [migratingId, setMigratingId] = useState<string | null>(null);

    // Form template groups state
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [templateGroups, setTemplateGroups] = useState<any[]>([]);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [expandedGroupTemplates, setExpandedGroupTemplates] = useState<Record<string, any[]>>({});
    const [loadingGroupId, setLoadingGroupId] = useState<string | null>(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [assignFormsGroup, setAssignFormsGroup] = useState<any | null>(null);

    // Folder rename state
    const [renamingFolder, setRenamingFolder] = useState<{ fileType: string; oldName: string } | null>(null);
    const [renameValue, setRenameValue] = useState('');
    const [isRenaming, setIsRenaming] = useState(false);

    // Preview state
    const [previewItem, setPreviewItem] = useState<DisplayResource | null>(null);

    const fetchAll = async () => {
        try {
            setIsLoading(true);
            const [newResources, legacyDocs, legacyVids, groups] = await Promise.all([
                listCompanyResourcesByCompanyId(companyId),
                listLegacyDocumentsByCompany(companyId),
                listLegacyVideosByCompany(companyId),
                listFormTemplateGroupsByCompany(companyId),
            ]);
            setTemplateGroups(groups);

            const allItems = [
                ...newResources.map(newResourceToDisplay),
                ...legacyDocs.map(legacyDocToDisplay),
                ...legacyVids.map(legacyVideoToDisplay),
            ];

            // Auto-deduplicate: normalize case-insensitive duplicate folder names within the same file type.
            // For each file type, if two folders differ only by case (e.g. "Safety" vs "safety"),
            // update the items to use the first-seen canonical name.
            const fixups: Promise<unknown>[] = [];
            const canonicalMap: Record<string, Record<string, string>> = {}; // fileType -> lowerName -> canonicalName

            for (const item of allItems) {
                if (!item.folderName || item.folderName === 'Unfiled') continue;
                const ft = item.fileType || 'Document';
                if (!canonicalMap[ft]) canonicalMap[ft] = {};
                const lower = item.folderName.toLowerCase();
                if (!canonicalMap[ft][lower]) {
                    // First occurrence — this becomes the canonical name
                    canonicalMap[ft][lower] = item.folderName;
                } else if (canonicalMap[ft][lower] !== item.folderName) {
                    // Duplicate with different casing — fix it
                    const canonical = canonicalMap[ft][lower];
                    item.folderName = canonical;
                    if (item._source === 'new') {
                        fixups.push(updateCompanyResourceMut({ id: item.id, folderName: canonical }));
                    } else if (item._source === 'legacy-doc') {
                        fixups.push(updateLegacyDocumentFolder(item._legacyId!, canonical));
                    } else if (item._source === 'legacy-video') {
                        fixups.push(updateLegacyVideoFolder(item._legacyId!, canonical));
                    }
                }
            }

            // Fire fixups in background (don't block UI)
            if (fixups.length > 0) {
                Promise.all(fixups).catch(err => console.error('Error auto-deduplicating folders:', err));
            }

            setDisplayItems(allItems);
        } catch (err) {
            console.error('Error fetching company resources:', err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => { if (companyId) fetchAll(); }, [companyId]);

    // Collect unique folder names from all display items (new + legacy)
    const existingFolders = [...new Set(
        displayItems.map(d => d.folderName).filter((f): f is string => !!f && f !== 'Unfiled')
    )].sort();

    // Group by the 5 design categories
    const categoryGroups = RESOURCE_CATEGORIES.map(cat => {
        const items = displayItems.filter(d => d.category === cat.name);
        const extraCount = cat.name === 'Forms & Templates' ? templateGroups.length : 0;
        return { ...cat, items, count: items.length + extraCount, subGroups: groupItemsByTypeAndFolder(items) };
    });

    const handleMigrate = async (item: DisplayResource) => {
        if (item._source === 'new') {
            setEditingResource(item._original || null);
            setShowModal(true);
            return;
        }
        setMigratingId(item.id);
        try {
            const fileType = item.fileType as any;
            const newRes = await createCompanyResource({
                companyId, title: item.title, description: null,
                category: 'Training Materials', fileType,
                contentFilePath: item.contentFilePath || null,
                uploadedByUserId: null, uploadedByUserName: null,
                folderName: item.folderName || null,
            });
            if (isTrainingCategory('Training Materials')) {
                const topicId = item.title;
                const style = mapFileTypeToTopicStyle(fileType);
                const newTopic = await createTrainingTopic({
                    companyId, topicId, style, description: item.title,
                    timeValue: 0, contentFilePath: item.contentFilePath || null,
                });
                if (!newRes || !newTopic) throw new Error('Failed to create company resource or training topic');
                await updateCompanyResourceMut({ id: newRes.id, trainingTopicId: newTopic.id });
                await updateTrainingTopic({ id: newTopic.id, companyResourceId: newRes.id } as any);
            }
            if (item._source === 'legacy-doc') await deleteLegacyDocument(item._legacyId!);
            else if (item._source === 'legacy-video') await deleteLegacyVideo(item._legacyId!);
            await fetchAll();
        } catch (err) {
            console.error('Error migrating legacy resource:', err);
        } finally {
            setMigratingId(null);
        }
    };

    const handleConfirmDeleteLegacy = async () => {
        if (!deletingLegacy || deletingLegacy._source === 'new') return;
        setIsDeletingLegacy(true);
        try {
            if (deletingLegacy.contentFilePath) {
                try { await remove({ path: deletingLegacy.contentFilePath }); } catch (err) { console.error('Error removing S3 file:', err); }
            }
            if (deletingLegacy._source === 'legacy-doc') await deleteLegacyDocument(deletingLegacy._legacyId!);
            else if (deletingLegacy._source === 'legacy-video') await deleteLegacyVideo(deletingLegacy._legacyId!);
            setDeletingLegacy(null);
            await fetchAll();
        } catch (err) {
            console.error('Error deleting legacy resource:', err);
        } finally {
            setIsDeletingLegacy(false);
        }
    };

    const handleCreateTrainingTopic = async (item: DisplayResource) => {
        if (item._source !== 'new' || !item._original) return;
        const resource = item._original;
        try {
            const topicId = uuidv4();
            const style = mapFileTypeToTopicStyle(resource.fileType as any);
            const newTopic = await createTrainingTopic({
                companyId: resource.companyId, topicId, style,
                description: resource.title, timeValue: 0,
                contentFilePath: resource.contentFilePath || null,
                contentUrl: resource.contentUrl || null,
            });
            if (!newTopic) throw new Error('Failed to create training topic');
            await updateCompanyResourceMut({ id: resource.id, trainingTopicId: newTopic.id });
            await updateTrainingTopic({ id: newTopic.id, companyResourceId: resource.id } as any);
            await fetchAll();
        } catch (err) {
            console.error('Error creating training topic:', err);
        }
    };

    // Form template group handlers
    const handleToggleGroupTemplates = async (groupId: string) => {
        if (expandedGroupTemplates[groupId]) {
            setExpandedGroupTemplates(prev => { const next = { ...prev }; delete next[groupId]; return next; });
            return;
        }
        setLoadingGroupId(groupId);
        try {
            const templates = await listFormTemplatesByGroup(groupId);
            setExpandedGroupTemplates(prev => ({ ...prev, [groupId]: templates }));
        } catch (err) {
            console.error('Error fetching templates for group:', err);
        } finally {
            setLoadingGroupId(null);
        }
    };

    const handleRemoveTemplateGroup = async (group: any) => {
        const name = group.name || 'this template group';
        if (!window.confirm(`Remove "${name}" from your company? This won't delete it for other companies.`)) return;
        try {
            await removeCompanyFromTemplateGroup(group.id, companyId, group.sharedWithCompanyIds || []);
            setTemplateGroups(prev => prev.filter(g => g.id !== group.id));
        } catch (err) {
            console.error('Error removing template group:', err);
        }
    };

    const startFolderRename = (fileType: string, folderName: string) => {
        setRenamingFolder({ fileType, oldName: folderName });
        setRenameValue(folderName);
    };

    const cancelFolderRename = () => {
        setRenamingFolder(null);
        setRenameValue('');
    };

    const handleFolderRename = async () => {
        if (!renamingFolder || !renameValue.trim()) return;
        const newName = renameValue.trim();
        const { fileType, oldName } = renamingFolder;

        // Case-insensitive check: if the new name matches the old name exactly, nothing to do
        if (newName === oldName) {
            cancelFolderRename();
            return;
        }

        // Find items in this file type with the old folder name
        const itemsToUpdate = displayItems.filter(
            d => d.fileType === fileType && d.folderName === oldName
        );

        // Check for case-insensitive duplicate folders within the same file type
        // e.g. "Safety" and "safety" should merge
        const existingFolderInType = displayItems.find(
            d => d.fileType === fileType
                && d.folderName
                && d.folderName !== oldName
                && d.folderName.toLowerCase() === newName.toLowerCase()
        );

        // Use the canonical name (the existing one if merging, otherwise the new name)
        const canonicalName = existingFolderInType ? existingFolderInType.folderName! : newName;

        if (existingFolderInType) {
            const mergeCount = itemsToUpdate.length;
            const confirmed = window.confirm(
                `A folder named "${existingFolderInType.folderName}" already exists in ${fileType}s. ` +
                `This will merge ${mergeCount} item${mergeCount !== 1 ? 's' : ''} into that folder. Continue?`
            );
            if (!confirmed) return;
        }

        setIsRenaming(true);
        try {
            const updates = itemsToUpdate.map(item => {
                if (item._source === 'new') {
                    return updateCompanyResourceMut({ id: item.id, folderName: canonicalName });
                } else if (item._source === 'legacy-doc') {
                    return updateLegacyDocumentFolder(item._legacyId!, canonicalName);
                } else if (item._source === 'legacy-video') {
                    return updateLegacyVideoFolder(item._legacyId!, canonicalName);
                }
                return Promise.resolve();
            });
            await Promise.all(updates);
            await fetchAll();
            cancelFolderRename();
        } catch (err) {
            console.error('Error renaming folder:', err);
        } finally {
            setIsRenaming(false);
        }
    };

    const handleSave = async () => { setShowModal(false); setEditingResource(null); await fetchAll(); };
    const handleDeleteComplete = async () => { setDeleteResource(null); await fetchAll(); };

    const formatDate = (dateStr?: string) => {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const renderItem = (item: DisplayResource) => (
        <div key={item.id} className="cr-resource-item">
            <span className="cr-resource-item-icon">{FILE_TYPE_ICONS[item.fileType] || '📝'}</span>
            <div className="cr-resource-item-info">
                <div className="cr-resource-item-title">
                    {item.title}
                    {item._source !== 'new' && (
                        <span className="cr-badge cr-badge--document" style={{ marginLeft: 8, fontSize: '0.7rem' }}>Legacy</span>
                    )}
                </div>
                <div className="cr-resource-item-date">{formatDate(item.createdAt)}</div>
            </div>
            <span className={`cr-badge ${FILE_TYPE_BADGE_CLASS[item.fileType] || 'cr-badge--document'}`}>
                {item.fileType}
            </span>
            {(item.contentFilePath || item.contentUrl) && (
                <button className="cr-btn--icon" title="Preview" onClick={() => setPreviewItem(item)}><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg></button>
            )}
            {item._source === 'new' && isTrainingCategory(item.category as CompanyResourceCategory) && (
                item.trainingTopicId
                    ? <span className="cr-linked-indicator">✅ Linked</span>
                    : isAdmin ? <button className="cr-link-action" onClick={() => handleCreateTrainingTopic(item)}>🔗 Create Topic</button> : null
            )}
            {isAdmin && (
                <div className="cr-resource-item-actions">
                    {item._source !== 'new' ? (
                        <>
                            <button className="cr-btn cr-btn--secondary" style={{ fontSize: '0.78rem', padding: '4px 10px' }}
                                onClick={() => handleMigrate(item)} disabled={migratingId === item.id}>
                                {migratingId === item.id ? '...' : '↗ Migrate'}
                            </button>
                            <Btn
                                variant="tertiary"
                                buttonSize="sm"
                                buttonClasses="btn-icon btn-icon-danger"
                                buttonIcon={<IconTrash />}
                                ariaLabel={`Delete ${item.title}`}
                                buttonDisabled={migratingId === item.id}
                                buttonOnClick={() => setDeletingLegacy(item)}
                            />
                        </>
                    ) : (
                        <>
                            <button className="cr-btn--icon" title="Edit" onClick={() => { setEditingResource(item._original!); setShowModal(true); }}>✏️</button>
                            <Btn
                                variant="tertiary"
                                buttonSize="sm"
                                buttonClasses="btn-icon btn-icon-danger"
                                buttonIcon={<IconTrash />}
                                ariaLabel={`Delete ${item.title}`}
                                buttonOnClick={() => setDeleteResource(item._original!)}
                            />
                        </>
                    )}
                </div>
            )}
        </div>
    );

    if (isLoading) {
        return <div className="cr-empty-state"><p className="cr-empty-state-text">Loading company resources...</p></div>;
    }

    const renderSectionBody = (section: (typeof categoryGroups)[number]) => (
        <div className="cr-category-section-body">
            {/* Form Template Groups — only in Forms & Templates category */}
            {section.name === 'Forms & Templates' && templateGroups.length > 0 && (
                <div>
                    <div className="cr-type-subheader">📋 Form Template Groups</div>
                    {templateGroups.map((group: any) => (
                        <div key={group.id}>
                            <div className="cr-resource-item" style={{ cursor: 'pointer' }}
                                onClick={() => handleToggleGroupTemplates(group.id)}>
                                <span className="cr-resource-item-icon">📂</span>
                                <div className="cr-resource-item-info">
                                    <div className="cr-resource-item-title">{group.name}</div>
                                    {group.description && (
                                        <div className="cr-resource-item-date">{group.description}</div>
                                    )}
                                </div>
                                <span className="cr-badge cr-badge--document">
                                    {loadingGroupId === group.id ? '...' : (expandedGroupTemplates[group.id] ? '▼' : '▶')}
                                </span>
                                {isAdmin && (
                                    <div className="cr-resource-item-actions" onClick={e => e.stopPropagation()}>
                                        <button className="cr-btn--icon" title="Assign forms"
                                            onClick={() => setAssignFormsGroup(group)}>📋</button>
                                        <button className="cr-btn--icon" title="Remove from company"
                                            onClick={() => handleRemoveTemplateGroup(group)}>🗑️</button>
                                    </div>
                                )}
                            </div>
                            {/* Expanded templates within group */}
                            {expandedGroupTemplates[group.id] && (
                                <div style={{ paddingLeft: 24, borderLeft: '2px solid #e8ecef', marginLeft: 18 }}>
                                    {expandedGroupTemplates[group.id].length === 0 ? (
                                        <div style={{ padding: '8px 12px', fontSize: '0.82rem', color: '#6b7280' }}>
                                            No templates in this group.
                                        </div>
                                    ) : (
                                        expandedGroupTemplates[group.id].map((tmpl: any) => (
                                            <div key={tmpl.id} className="cr-resource-item">
                                                <span className="cr-resource-item-icon">📝</span>
                                                <div className="cr-resource-item-info">
                                                    <div className="cr-resource-item-title">{tmpl.templateName}</div>
                                                    <div className="cr-resource-item-date">
                                                        {tmpl.inspectionForm && <span className="cr-badge cr-badge--part" style={{ marginRight: 4 }}>Inspection</span>}
                                                        {tmpl.dailyServiceLogForm && <span className="cr-badge cr-badge--fluid" style={{ marginRight: 4 }}>Daily Service</span>}
                                                        {tmpl.pmServiceScheduleForm && <span className="cr-badge cr-badge--excel">PM Service</span>}
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Regular resources in this category */}
            {section.items.length === 0 && (section.name !== 'Forms & Templates' || templateGroups.length === 0) ? (
                <div className="cr-empty-state" style={{ padding: '20px' }}>
                    <p className="cr-empty-state-text">No resources in this category yet.</p>
                </div>
            ) : (
                section.subGroups.map(sg => (
                    <div key={sg.fileType}>
                        {/* Show file type sub-header when multiple types exist */}
                        {section.subGroups.length > 1 && (
                            <div className="cr-type-subheader">
                                {FILE_TYPE_ICONS[sg.fileType]} {sg.fileType}s
                            </div>
                        )}
                        {sg.folders.map(folder => (
                            <div key={folder.name}>
                                {(sg.folders.length > 1 || folder.name !== 'Unfiled') && (
                                    <div className="cr-folder-header">
                                        {renamingFolder && renamingFolder.fileType === sg.fileType && renamingFolder.oldName === folder.name ? (
                                            <div className="cr-folder-rename-row">
                                                <span>📂</span>
                                                <input
                                                    type="text"
                                                    className="cr-folder-rename-input"
                                                    value={renameValue}
                                                    onChange={e => setRenameValue(e.target.value)}
                                                    onKeyDown={e => {
                                                        if (e.key === 'Enter') handleFolderRename();
                                                        if (e.key === 'Escape') cancelFolderRename();
                                                    }}
                                                    autoFocus
                                                    disabled={isRenaming}
                                                />
                                                <button
                                                    className="cr-folder-rename-save"
                                                    onClick={handleFolderRename}
                                                    disabled={isRenaming || !renameValue.trim()}
                                                >
                                                    {isRenaming ? '...' : '✓'}
                                                </button>
                                                <button
                                                    className="cr-folder-rename-cancel"
                                                    onClick={cancelFolderRename}
                                                    disabled={isRenaming}
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        ) : (
                                            <>
                                                📂 {folder.name} ({folder.items.length})
                                                {isAdmin && folder.name !== 'Unfiled' && (
                                                    <button
                                                        className="cr-folder-rename-btn"
                                                        onClick={() => startFolderRename(sg.fileType, folder.name)}
                                                        title="Rename folder"
                                                    >
                                                        ✎
                                                    </button>
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}
                                {folder.items.map(renderItem)}
                            </div>
                        ))}
                    </div>
                ))
            )}
        </div>
    );

    const accordionItems: AccordionCardItem[] = categoryGroups.map((section) => ({
        id: section.name,
        title: section.name,
        className: 'cr-resource-category-section',
        style: { '--cr-category-header-bg': section.color } as CSSProperties,
        meta: <span className="cr-category-section-count">{section.count} total</span>,
        children: renderSectionBody(section),
    }));

    return (
        <div>
            <div className="cr-toolbar">
                {isAdmin && (
                    <Btn
                        variant="primary"
                        buttonSize="sm"
                        buttonIcon={<IconPlus />}
                        buttonText="Add Company Resource"
                        buttonOnClick={() => { setEditingResource(null); setShowModal(true); }}
                    />
                )}
            </div>

            <AccordionCard
                className="cr-resource-category-cards"
                expandMode="multiple"
                expandedIds={expandedIds}
                onExpandedIdsChange={setExpandedIds}
                items={accordionItems}
            />

            {showModal && (
                <ResourceModal isOpen={showModal}
                    onClose={() => { setShowModal(false); setEditingResource(null); }}
                    onSave={handleSave} editResource={editingResource} companyId={companyId}
                    existingFolders={existingFolders} />
            )}
            <DeleteResourceModal
                resource={deleteResource}
                onClose={() => setDeleteResource(null)}
                onDelete={handleDeleteComplete}
                companyId={companyId}
            />
            <DeleteLegacyResourceModal
                item={deletingLegacy}
                isDeleting={isDeletingLegacy}
                onDismiss={() => { if (!isDeletingLegacy) setDeletingLegacy(null); }}
                onConfirmDelete={handleConfirmDeleteLegacy}
            />
            {assignFormsGroup && (
                <AssignFormsModal
                    isOpen={!!assignFormsGroup}
                    onClose={() => setAssignFormsGroup(null)}
                    group={assignFormsGroup}
                    companyId={companyId}
                    onSaved={() => {
                        // Refresh the expanded templates for this group
                        if (assignFormsGroup) {
                            setExpandedGroupTemplates(prev => { const next = { ...prev }; delete next[assignFormsGroup.id]; return next; });
                        }
                        fetchAll();
                    }}
                />
            )}
            <TopicContentViewerModal
                isOpen={!!previewItem}
                onClose={() => setPreviewItem(null)}
                topic={previewItem ? {
                    id: previewItem.id,
                    companyId,
                    topicId: previewItem.title,
                    style: previewItem.fileType === 'Video' ? 'Video'
                        : previewItem.fileType === 'PDF' ? 'PDF'
                            : previewItem.fileType === 'Image' ? 'Poster'
                                : 'Other',
                    description: previewItem.title,
                    timeValue: 0,
                    contentFilePath: previewItem.contentFilePath || null,
                    contentUrl: previewItem.contentUrl || null,
                } : null}
            />
        </div>
    );
};
